import * as React from "react";
import { useClerk } from "@clerk/clerk-react";

import { deactivateCurrentDevicePush } from "@/features/push";

export const TENANT_SIGN_OUT_MARKER_KEY = "tenant-sign-out-at";
const TENANT_SIGN_OUT_MARKER_MAX_AGE_MS = 30_000;

export function markTenantSignOutStarted() {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(TENANT_SIGN_OUT_MARKER_KEY, String(Date.now()));
}

export function clearTenantSignOutMarker() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(TENANT_SIGN_OUT_MARKER_KEY);
}

export function isTenantSignOutInProgress() {
  if (typeof window === "undefined") return false;
  const raw = window.sessionStorage.getItem(TENANT_SIGN_OUT_MARKER_KEY);
  if (!raw) return false;

  const startedAt = Number(raw);
  if (!Number.isFinite(startedAt)) {
    clearTenantSignOutMarker();
    return false;
  }

  if (Date.now() - startedAt > TENANT_SIGN_OUT_MARKER_MAX_AGE_MS) {
    clearTenantSignOutMarker();
    return false;
  }

  return true;
}

type UseAppSignOutOptions = {
  onAfterSignOut?: () => void;
  redirectPath?: string;
};

export function useAppSignOut(options: UseAppSignOutOptions = {}) {
  const clerk = useClerk();
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  const signOut = React.useCallback(async () => {
    if (isSigningOut) return;

    setIsSigningOut(true);
    markTenantSignOutStarted();
    try {
      // Deactivate ONLY this device's push subscription before signing out, while we are
      // still authenticated. Best-effort: never blocks sign-out, never touches other devices.
      await deactivateCurrentDevicePush();

      const redirectUrl =
        options.redirectPath && typeof window !== "undefined"
          ? new URL(options.redirectPath, window.location.origin).toString()
          : undefined;
      await clerk.signOut(redirectUrl ? { redirectUrl } : undefined);
    } finally {
      options.onAfterSignOut?.();
      setIsSigningOut(false);
    }
  }, [clerk, isSigningOut, options]);

  return { signOut, isSigningOut };
}
